import React from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { ReleasesPage } from '@help/pages/ReleasesPage.jsx'
import { NotificationsPage } from '@help/pages/NotificationsPage.jsx'
import { GuidePage } from '@help/pages/GuidePage.jsx'
import { FAQPage } from '@help/pages/FAQPage.jsx'
import adminGuideHtml from 'virtual:wiki/guide-admins'
import securityComplianceHtml from 'virtual:wiki/security-compliance'

// Admin-portal docs surface. Visually mirrors frontend/src/help/DocsLayout.jsx
// (underline tab nav, 720px content column), but scoped to the tabs admin
// users need: release notes, notification history, the two admin guides, and
// an admin-filtered FAQ. See PR #388 for the broader admin-portal context.
const NAV_ITEMS = [
  { id: 'releases', label: "What's new", path: '/docs/releases' },
  { id: 'notifications', label: 'Notifications', path: '/docs/notifications' },
  { id: 'admin', label: 'For admins', path: '/docs/admin' },
  { id: 'security-compliance', label: 'For InfoSec', path: '/docs/security-compliance' },
  { id: 'faq', label: 'FAQ', path: '/docs/faq' },
]

export function AdminDocsLayout() {
  const nav = useNavigate()
  const location = useLocation()
  const handleMdLink = () => { /* no cross-page markdown links inside admin docs */ }

  return (
    <div style={styles.wrapper}>
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            return (
              <button
                key={item.id}
                style={{ ...styles.tab, ...(active ? styles.tabActive : {}) }}
                onClick={() => nav(item.path)}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      </nav>
      <div style={styles.content}>
        <Routes>
          <Route index element={<Navigate to="releases" replace />} />
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route
            path="admin"
            element={
              <GuidePage
                title="Guide for admins"
                subtitle="Managing your organization's Soundcheck instance."
                html={adminGuideHtml}
                onMdLink={handleMdLink}
              />
            }
          />
          <Route
            path="security-compliance"
            element={
              <GuidePage
                title="For InfoSec"
                subtitle="For ISO / IPO and auditors. Access control, encryption, Cognito policies, and known gaps."
                html={securityComplianceHtml}
                onMdLink={handleMdLink}
              />
            }
          />
          <Route path="faq" element={<FAQPage audience="admin" />} />
          <Route path="*" element={<Navigate to="releases" replace />} />
        </Routes>
      </div>
    </div>
  )
}

const styles = {
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    background: 'var(--bg)',
  },
  nav: {
    padding: '16px 32px 4px',
    borderBottom: '1px solid var(--border)',
  },
  navInner: {
    display: 'flex',
    gap: '4px',
    maxWidth: '720px',
    margin: '0 auto',
    overflowX: 'auto',
  },
  tab: {
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  tabActive: {
    color: 'var(--text-primary)',
    fontWeight: 600,
    borderBottomColor: 'var(--text-primary)',
  },
  content: {
    padding: '24px 32px',
    maxWidth: '720px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
}
